/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon, BoxSpinnerView } from '@components/atoms'
import { List, ListItemButton } from '@components/molecules'
import { AccessGroupListItem } from '@components/systems/accessControl/AccessGroupListItem'

import { useAppSelector, CrudType } from '@redux/index'
import { AccessGroupType } from '@schemas'
import { grey, blue } from '@mui/material/colors'

import { ApiGetDataStatus } from '@redux/index'

interface PropsType {
	groupList: AccessGroupType[]
	status: ApiGetDataStatus
	onGroupClick: (aGroup: AccessGroupType) => void
	onAddButtonClick?: () => void
	selectedGroupIds?: string[]
	showAddButton?: boolean
	rightAccessory?: (aGroup: AccessGroupType, isChecked: boolean) => JSX.Element
}

export const AccessGroupList = (props: PropsType) => {
	const { groupList, status, onAddButtonClick } = props

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)

	// variables
	const showAddButton = props.showAddButton ?? false

	const onGroupClick = (aGroup: AccessGroupType) => {
		props.onGroupClick(aGroup)
	}

	const RenderAddButton = ({ style }: { style?: React.CSSProperties }) => {
		return (
			<>
				{crud.includes('C') && (
					<ListItemButton
						className="listItem addItem"
						css={{ ...style }}
						onClick={onAddButtonClick}
					>
						<View className="addView">
							<Icon name="plus-thick" className="addIcon" />
						</View>
					</ListItemButton>
				)}
			</>
		)
	}

	const RightAccessory = ({
		group,
		isChecked,
	}: {
		group: AccessGroupType
		isChecked: boolean | undefined
	}) => {
		return (
			<>{props.rightAccessory ? props.rightAccessory(group, isChecked ?? false) : null}</>
		)
	}

	return (
		<View css={style}>
			{(status === 'init' || status === 'loading') && (
				<View className="spinnerView">
					<BoxSpinnerView />
				</View>
			)}

			<View>
				<View className="listOuter">
					<List>
						{groupList.map(aGroup => {
							const isChecked: boolean | undefined = props.selectedGroupIds
								? props.selectedGroupIds.includes(aGroup.id)
								: undefined

							return (
								<View key={aGroup.id} className="listItemView">
									<ListItemButton
										className={`listItem ${isChecked ? 'checked' : ''}`}
										onClick={() => onGroupClick(aGroup)}
									>
										<AccessGroupListItem group={aGroup} />
									</ListItemButton>

									{props.rightAccessory && (
										<View className={`rightAccessory ${isChecked ? 'checked' : ''}`}>
											<RightAccessory group={aGroup} isChecked={isChecked} />
										</View>
									)}
								</View>
							)
						})}

						<View className="listItemView">{showAddButton && <RenderAddButton />}</View>
					</List>
				</View>
			</View>
		</View>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	height: '100%',
	background: grey[50],
	border: '1px solid',
	borderColor: grey[200],
	borderRadius: '5px',

	'.spinnerView': {
		position: 'absolute',
		zIndex: 9,
		width: '100%',
		height: '100%',
		top: 0,
		background: '#FFF',
		opacity: 0.5,
	},

	'.accessGroupListEmptyView': {
		width: '100%',
		height: '100%',

		'.addButtonOuter': {
			position: 'absolute',
			width: '100%',
			height: '100%',
			top: 0,
			padding: '0 1rem',

			'.listItem': {
				position: 'absolute',
				width: 'calc(100% - 2rem)',
				bottom: '3rem',
				marginBottom: '0.2rem',
				background: '#FFF',
				borderRadius: '5px',
				border: '1px solid',
				borderColor: grey[300],

				'&.addItem': {
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					background: grey[200],
					border: 0,

					'.addView': {
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',

						'.addIcon': {
							color: grey[600],
						},
					},
				},
			},
		},
	},

	'.listOuter': {
		padding: '0 0.5rem',

		'.listItemView': {
			position: 'relative',

			'.rightAccessory': {
				position: 'absolute',
				zIndex: 5,
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				top: 0,
				bottom: 0,
				right: 0,

				'.listItemEdit': {
					position: 'absolute',
					height: '2rem',
					right: '0.5rem',
					top: 0,
					bottom: 0,
					margin: 'auto',
					opacity: 0,
				},

				'&.checked': {
					'.listItemEdit': {
						opacity: 1,
					},
				},
			},

			'&:hover': {
				'.listItemEdit': {
					opacity: 1,
				},
			},

			'.listItem': {
				marginBottom: '0.2rem',
				background: '#FFF',
				borderRadius: '5px',
				border: '1px solid',
				borderColor: grey[300],

				'&.checked': {
					background: '#f1f9ff',
					color: blue[700],
				},

				'&.addItem': {
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					background: grey[200],
					border: 0,

					'.addView': {
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',

						'.addIcon': {
							color: grey[600],
						},
					},
				},
			},
		},
	},
})
